import {
  madBuyLineItemWeekDays,
  type MadBuyLineItemDayTimeActivity,
  type MadBuyLineItemWeekDay,
} from './lineItemsCoreModel';
import { getDateTimeParts, isValidTimezone } from './lineItemDateTimeModel';
import { normalizeDayTimeActivities } from './lineItemScheduleModel';

type ScheduleDateParts = { year: number; month: number; day: number };

export function convertMadBuyLineItemScheduleTimezone(
  activities: MadBuyLineItemDayTimeActivity[] | undefined,
  fromTimezone = 'UTC',
  toTimezone = 'UTC',
  referenceDate?: string,
) {
  const normalized = normalizeDayTimeActivities(activities);
  if (!isValidTimezone(fromTimezone) || !isValidTimezone(toTimezone)) {
    throw new RangeError('Invalid schedule timezone');
  }

  if (!normalized.length || fromTimezone === toTimezone) {
    return normalized;
  }

  const scheduleAnchorByDay = resolveScheduleAnchors(referenceDate, fromTimezone);
  const hoursByDay = new Map<MadBuyLineItemWeekDay, Set<number>>();
  normalized.forEach((activity) => {
    for (let hour = activity.startHour; hour <= activity.endHour; hour += 1) {
      const converted = convertScheduleHour(
        activity.day,
        hour,
        fromTimezone,
        toTimezone,
        scheduleAnchorByDay,
      );
      const hours = hoursByDay.get(converted.day) ?? new Set<number>();
      hours.add(converted.hour);
      hoursByDay.set(converted.day, hours);
    }
  });

  return madBuyLineItemWeekDays.flatMap((day) => collapseScheduleHours(day, hoursByDay.get(day)));
}

function convertScheduleHour(
  day: MadBuyLineItemWeekDay,
  hour: number,
  fromTimezone: string,
  toTimezone: string,
  scheduleAnchorByDay: Record<MadBuyLineItemWeekDay, ScheduleDateParts>,
) {
  const anchor = scheduleAnchorByDay[day];
  const instant = zonedWallTimeToDate({ ...anchor, hour }, fromTimezone);
  const parts = getDateTimeParts(instant, toTimezone);
  return {
    day: resolveWeekDay(parts.year, parts.month, parts.day),
    hour: parts.hour,
  };
}

function resolveScheduleAnchors(referenceDate: string | undefined, timezone: string) {
  const reference = parseReferenceDate(referenceDate, timezone);
  if (!reference) {
    throw new RangeError('A valid schedule reference date is required for timezone conversion');
  }
  const referenceUtcDate = Date.UTC(reference.year, reference.month - 1, reference.day);
  const daysFromMonday = (new Date(referenceUtcDate).getUTCDay() + 6) % 7;
  const monday = new Date(referenceUtcDate - daysFromMonday * 24 * 60 * 60 * 1000);

  return Object.fromEntries(
    madBuyLineItemWeekDays.map((day, index) => {
      const date = new Date(monday.getTime() + index * 24 * 60 * 60 * 1000);
      return [
        day,
        {
          year: date.getUTCFullYear(),
          month: date.getUTCMonth() + 1,
          day: date.getUTCDate(),
        },
      ];
    }),
  ) as Record<MadBuyLineItemWeekDay, ScheduleDateParts>;
}

function parseReferenceDate(referenceDate: string | undefined, timezone: string) {
  if (!referenceDate) {
    return null;
  }

  const dateTimeInputMatch = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}$/.exec(referenceDate);
  if (dateTimeInputMatch?.[1] && dateTimeInputMatch[2] && dateTimeInputMatch[3]) {
    return {
      year: Number(dateTimeInputMatch[1]),
      month: Number(dateTimeInputMatch[2]),
      day: Number(dateTimeInputMatch[3]),
    };
  }

  const instant = new Date(referenceDate);
  if (Number.isNaN(instant.getTime())) {
    return null;
  }

  const parts = getDateTimeParts(instant, timezone);
  return { year: parts.year, month: parts.month, day: parts.day };
}

function collapseScheduleHours(day: MadBuyLineItemWeekDay, hours: Set<number> | undefined) {
  const normalizedHours = Array.from(hours ?? []).sort((left, right) => left - right);
  const ranges: MadBuyLineItemDayTimeActivity[] = [];
  let rangeStart: number | undefined;
  let previousHour: number | undefined;

  normalizedHours.forEach((hour) => {
    if (rangeStart === undefined || previousHour === undefined || hour !== previousHour + 1) {
      if (rangeStart !== undefined && previousHour !== undefined) {
        ranges.push({ day, restrictedHours: true, startHour: rangeStart, endHour: previousHour });
      }
      rangeStart = hour;
    }
    previousHour = hour;
  });

  if (rangeStart !== undefined && previousHour !== undefined) {
    ranges.push({ day, restrictedHours: true, startHour: rangeStart, endHour: previousHour });
  }

  return ranges;
}

function zonedWallTimeToDate(
  value: { year: number; month: number; day: number; hour: number },
  timezone: string,
) {
  const desired = Date.UTC(value.year, value.month - 1, value.day, value.hour, 0, 0);
  const sampleInstants = [desired - 24 * 60 * 60 * 1000, desired, desired + 24 * 60 * 60 * 1000];
  const candidates = new Set<number>();

  sampleInstants.forEach((sample) => {
    const parts = getDateTimeParts(new Date(sample), timezone);
    const renderedAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, 0, 0);
    const candidate = desired - (renderedAsUtc - sample);
    const renderedCandidate = getDateTimeParts(new Date(candidate), timezone);
    if (scheduleDateTimePartsEqual(renderedCandidate, value)) {
      candidates.add(candidate);
    }
  });

  const earliest = Array.from(candidates).sort((left, right) => left - right)[0];
  if (earliest === undefined) {
    throw new RangeError('The scheduled local hour does not exist in the selected timezone');
  }
  return new Date(earliest);
}

function scheduleDateTimePartsEqual(
  left: { year: number; month: number; day: number; hour: number },
  right: { year: number; month: number; day: number; hour: number },
) {
  return (
    left.year === right.year &&
    left.month === right.month &&
    left.day === right.day &&
    left.hour === right.hour
  );
}

function resolveWeekDay(year: number, month: number, day: number): MadBuyLineItemWeekDay {
  const index = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return madBuyLineItemWeekDays[(index + 6) % 7] ?? 'MONDAY';
}
